import { axiosBaseQuery } from "@picovico/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import type { WhatsAppMe, WhatsAppMeUpdateBody } from "custom-types";
import { readWhatsAppMe } from "utils/whatsapp";

/**
 * The customer's WhatsApp options (claude/design/whatsapp-messaging/02 §11.3).
 * amphitheater checks the `whatsapp` flag in the view and answers 403
 * `FEATURE_DISABLED` when it is off; callers read any refusal as "off"
 * (`useWhatsAppMe`), so both requests are `silentFeatureDisabled`.
 * Both answers are read leniently into a `WhatsAppMe` (envelope or bare).
 */
export const whatsappApi = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ["WhatsAppMe"],
  reducerPath: "whatsappApi",
  endpoints: (builder) => ({
    getWhatsappMe: builder.query<WhatsAppMe, void>({
      query: () => ({
        name: "whatsapp_me",
        method: "GET",
        silentFeatureDisabled: true,
      }),
      transformResponse: (response: unknown) => readWhatsAppMe(response),
      providesTags: ["WhatsAppMe"],
      // Gone with its last screen (pvapp's autoDispose): each checkout or
      // profile visit reads it fresh, and nothing outlives a sign-out.
      keepUnusedDataFor: 0,
    }),
    /** The profile toggle: `{offers_opt_in}`; without `number` the server uses the profile mobile. */
    updateWhatsappMe: builder.mutation<WhatsAppMe, WhatsAppMeUpdateBody>({
      query: (data) => ({
        name: "whatsapp_me",
        method: "PATCH",
        data,
        silentFeatureDisabled: true,
      }),
      transformResponse: (response: unknown) => readWhatsAppMe(response),
      // The answer is the new state: no second GET. The entry exists while
      // the toggle shows (it renders from it).
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            whatsappApi.util.updateQueryData(
              "getWhatsappMe",
              undefined,
              () => data,
            ),
          );
        } catch {
          // The toggle shows the refusal and keeps the old value.
        }
      },
    }),
  }),
});

export const { useGetWhatsappMeQuery, useUpdateWhatsappMeMutation } =
  whatsappApi;
